import { VisuallyHidden, withContext } from '@piggybank/core';
import { FieldContext } from '@piggybank/form';
import { CurrencyInput as PiggybankCurrencyInput } from '@piggybank/form/lib/es/components/CurrencyInput/CurrencyInput';
import { Select as PiggybankSelect } from '@piggybank/form/lib/es/components/Select/Select';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import T from 'prop-types';
import React, { useMemo, useState } from 'react';
import {
  Translation,
  TranslationScope,
  useCultureConfiguration,
} from '../../context';
import {
  formatCurrency,
  getCurrencySymbol,
  getSchemaAtCursor,
  localizationKeyPath,
  parseCurrency,
} from '../../utils';
import styles from './style.css';

export const CurrencySelectionInput = ({
  clearAmountOnCurrencyChange,
  context,
  name,
  onChange,
  validate,
  value,
  ...props
}) => {
  const culture = useCultureConfiguration();
  const schemaNode = getSchemaAtCursor(context.schemaPath, validate.schema);

  const [amount, setAmount] = useState(
    get(value, 'amount', get(schemaNode, 'default.amount', ''))
  );

  const defaultCurrency = useMemo(
    () =>
      parseCurrency(
        get(schemaNode, 'default.currency', get(culture, 'currency'))
      ),
    [schemaNode, culture]
  );

  const [currencyCode, setCurrencyCode] = useState(
    get(value, 'currency', defaultCurrency.code)
  );

  const sanitizedKeyPath = localizationKeyPath(context.keyPath);
  const symbol = useMemo(() => getCurrencySymbol(currencyCode), [currencyCode]);

  const handleCurrencyCodeChange = ({ event, value }) => {
    if (value !== currencyCode) {
      setCurrencyCode(value);
      const newAmount = clearAmountOnCurrencyChange === true ? '' : amount;
      setAmount(newAmount);

      onChange({
        value: {
          currency: value,
          amount: newAmount,
        },
        event,
      });
    }
  };

  const handleAmountChange = ({ event, value }) => {
    setAmount(value);

    onChange({
      value: {
        currency: currencyCode,
        amount: value,
      },
      event,
    });
  };

  return (
    <TranslationScope path={sanitizedKeyPath}>
      <div className={styles.currencyGroup}>
        <Translation path="currency">
          {({ optionLabels, screenReader }) => {
            const options = get(schemaNode, 'properties.currency.enum', []).map(
              (value, i) => ({
                label: optionLabels[i],
                value,
              })
            );

            return (
              <>
                <VisuallyHidden
                  component="label"
                  htmlFor={`${name}.currency-field`}
                >
                  {screenReader.code}
                </VisuallyHidden>
                <PiggybankSelect
                  {...props}
                  name={`${name}.currency`}
                  onChange={handleCurrencyCodeChange}
                  options={options}
                  value={currencyCode}
                />

                <VisuallyHidden
                  component="label"
                  htmlFor={`${name}.amount-field`}
                >
                  {screenReader.amount}
                </VisuallyHidden>
                <PiggybankCurrencyInput
                  {...props}
                  {...(get(schemaNode, 'properties.amount.minimum', null)
                    ? {
                        minLength: String(schemaNode.properties.amount.minimum)
                          .length,
                      }
                    : {})}
                  {...(get(schemaNode, 'properties.amount.maximum', null)
                    ? {
                        maxLength: String(schemaNode.properties.amount.maximum)
                          .length,
                      }
                    : {})}
                  name={`${name}.amount`}
                  onChange={handleAmountChange}
                  symbol={symbol}
                  value={String(amount)}
                />
              </>
            );
          }}
        </Translation>
      </div>
    </TranslationScope>
  );
};

CurrencySelectionInput.formStructure = ['currency', 'amount'];

CurrencySelectionInput.reviewFormatter = ({ culture, formData }) => {
  if (isEmpty(formData)) return '';

  return formatCurrency(
    formData.currency || get(culture, 'currency.code'),
    formData.amount,
    `${culture.lang}-${culture.countryCode}`
  );
};

CurrencySelectionInput.propTypes = {
  value: T.object,
  name: T.string,
  invalid: T.bool,
  required: T.bool,
  describers: T.string,
  onChange: T.func,
  context: T.object,
  validate: T.func,
  clearAmountOnCurrencyChange: T.bool,
};

export default withContext(FieldContext, props =>
  pick(props, [
    'value',
    'onChange',
    'onBlur',
    'invalid',
    'required',
    'describers',
    'name',
  ])
)(CurrencySelectionInput);
